import React, { useCallback, useEffect, useState } from 'react';
import { buildTrackingApiUrl } from '../config/brand';
import { useLanguage } from '../i18n/LanguageContext';

const HISTORY_KEY = 'betaexpressTrackingHistory';
const KAZAKH_MONTHS = [
  'қаңтар',
  'ақпан',
  'наурыз',
  'сәуір',
  'мамыр',
  'маусым',
  'шілде',
  'тамыз',
  'қыркүйек',
  'қазан',
  'қараша',
  'желтоқсан',
];

function readHistory() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(HISTORY_KEY));
    return Array.isArray(stored) ? stored.slice(0, 5) : [];
  } catch (_error) {
    return [];
  }
}

function saveToHistory(code) {
  const previous = readHistory();
  const next = [
    { code, time: new Date().toISOString() },
    ...previous.filter((item) => item.code !== code),
  ].slice(0, 5);
  window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  return next;
}

function formatDate(value, withTime = false, locale = 'ru-RU', noInfo = 'Нет информации') {
  if (!value) return noInfo;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  if (locale.toLowerCase().startsWith('kk')) {
    const datePart = `${date.getFullYear()} жылғы ${date.getDate()} ${KAZAKH_MONTHS[date.getMonth()]}`;
    if (!withTime) return datePart;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${datePart}, ${hours}:${minutes}`;
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
}

function displayWeight(weight, unit = 'кг', noInfo = 'Нет информации') {
  const numericWeight = Number(weight);
  return Number.isFinite(numericWeight) ? `${numericWeight.toFixed(2)} ${unit}` : noInfo;
}

function TrackingResult({ destination }) {
  const { language, t } = useLanguage();
  const tracks = Array.isArray(destination.destination_tracks) ? destination.destination_tracks : [];
  const deliveries = Array.isArray(destination.deliveries) ? destination.deliveries : [];
  const successfulDelivery = deliveries.find((delivery) => delivery.code === 'success');
  const locale = language === 'kk' ? 'kk-KZ' : language === 'en' ? 'en-GB' : 'ru-RU';
  const eventLabel = tracks.length === 1 ? t.trackingPage.events[0] : t.trackingPage.events[2];

  return (
    <div className="tracking-result" aria-live="polite">
      <div className="tracking-result__head">
        <div>
          <span className="eyebrow">{t.trackingPage.waybill}</span>
          <h2>{destination.code}</h2>
        </div>
        <span className={`status-pill${successfulDelivery ? ' status-pill--success' : ''}`}>
          {successfulDelivery ? t.trackingPage.delivered : t.trackingPage.inTransit}
        </span>
      </div>

      <dl className="shipment-grid">
        <div><dt>{t.trackingPage.receiver}</dt><dd>{destination.receiver || t.common.noInfo}</dd></div>
        <div><dt>{t.trackingPage.from}</dt><dd>{destination.origin_region || destination.startpoint || t.common.noInfo}</dd></div>
        <div><dt>{t.trackingPage.to}</dt><dd>{destination.destination_region || destination.endpoint || t.common.noInfo}</dd></div>
        <div><dt>{t.trackingPage.pieces}</dt><dd>{destination.quantity || t.common.noInfo}</dd></div>
        <div><dt>{t.trackingPage.weight}</dt><dd>{displayWeight(destination.weight, t.trackingPage.weightUnit, t.common.noInfo)}</dd></div>
        <div><dt>{t.trackingPage.plannedDate}</dt><dd>{formatDate(destination.expiration, false, locale, t.common.noInfo)}</dd></div>
      </dl>

      {successfulDelivery && (
        <div className="delivery-success">
          <span className="delivery-success__icon" aria-hidden="true">✓</span>
          <div>
            <strong>{t.trackingPage.deliveredTitle}</strong>
            <p>
              {successfulDelivery.receiver_name || t.trackingPage.defaultReceiver}
              {successfulDelivery.stamp ? `, ${formatDate(successfulDelivery.stamp, true, locale, t.common.noInfo)}` : ''}
            </p>
          </div>
        </div>
      )}

      <div className="timeline-section">
        <div className="section-heading section-heading--compact">
          <div>
            <span className="eyebrow">{t.trackingPage.route}</span>
            <h3>{t.trackingPage.history}</h3>
          </div>
          <span>{tracks.length} {eventLabel}</span>
        </div>
        {tracks.length > 0 ? (
          <ol className="tracking-timeline">
            {tracks.map((track, index) => (
              <li key={`${track.code}-${track.created_at}-${index}`} className={index === 0 ? 'tracking-timeline__latest' : ''}>
                <span className="timeline-dot" aria-hidden="true" />
                <div>
                  <div className="timeline-meta">
                    <strong>{track.description || track.code}</strong>
                    <time>{formatDate(track.created_at, true, locale, t.common.noInfo)}</time>
                  </div>
                  {track.branch && <p>{track.branch}</p>}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="empty-state">{t.trackingPage.emptyTimeline}</p>
        )}
      </div>
    </div>
  );
}

function TrackingPanel({ initialCode = '' }) {
  const { language, t } = useLanguage();
  const [code, setCode] = useState(initialCode);
  const [history, setHistory] = useState(() => (typeof window === 'undefined' ? [] : readHistory()));
  const [destination, setDestination] = useState(null);
  const [status, setStatus] = useState(initialCode ? 'loading' : 'idle');
  const [message, setMessage] = useState('');

  const track = useCallback(async (rawCode) => {
    const normalizedCode = rawCode.trim();
    if (!normalizedCode) {
      setStatus('error');
      setMessage('required');
      return;
    }

    setStatus('loading');
    setMessage('');
    setDestination(null);
    setCode(normalizedCode);
    setHistory(saveToHistory(normalizedCode));

    try {
      const response = await fetch(buildTrackingApiUrl(normalizedCode, language));
      const data = await response.json();
      if (!response.ok || data.error || !data.destination) {
        throw new Error('not_found');
      }
      setDestination(data.destination);
      setStatus('success');
    } catch (_error) {
      setStatus('error');
      setMessage('notFound');
    }
  }, [language]);

  const locale = language === 'kk' ? 'kk-KZ' : language === 'en' ? 'en-GB' : 'ru-RU';

  useEffect(() => {
    if (initialCode) track(initialCode);
  }, [initialCode, track]);

  const handleSubmit = (event) => {
    event.preventDefault();
    track(code);
  };

  return (
    <div className="tracking-workspace">
      <aside className="tracking-sidebar">
        <form onSubmit={handleSubmit} className="tracking-form">
          <label htmlFor="tracking-code">{t.trackingPage.inputLabel}</label>
          <div className="tracking-input-row">
            <input
              id="tracking-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder={t.trackingPage.inputPlaceholder}
              autoComplete="off"
              inputMode="text"
            />
            <button type="submit" className="button button--primary" disabled={status === 'loading'}>
              {status === 'loading' ? t.common.searching : t.common.search}
            </button>
          </div>
          <p className="field-hint">{t.trackingPage.hint}</p>
        </form>

        {history.length > 0 && (
          <div className="tracking-history">
            <p className="tracking-history__title">{t.trackingPage.recent}</p>
            {history.map((item) => (
              <button key={item.code} type="button" onClick={() => track(item.code)}>
                <span>{item.code}</span>
                <small>{formatDate(item.time, false, locale, t.common.noInfo)}</small>
              </button>
            ))}
          </div>
        )}
      </aside>

      <div className="tracking-content">
        {status === 'idle' && (
          <div className="tracking-placeholder">
            <span className="tracking-placeholder__mark" aria-hidden="true">↗</span>
            <h2>{t.trackingPage.placeholderTitle}</h2>
            <p>{t.trackingPage.placeholderText}</p>
          </div>
        )}
        {status === 'loading' && (
          <div className="tracking-placeholder" aria-live="polite">
            <span className="loader" aria-hidden="true" />
            <h2>{t.trackingPage.loadingTitle}</h2>
            <p>{t.trackingPage.loadingText}</p>
          </div>
        )}
        {status === 'error' && (
          <div className="tracking-placeholder tracking-placeholder--error" role="alert">
            <span className="tracking-placeholder__mark" aria-hidden="true">!</span>
            <h2>{t.trackingPage.errorTitle}</h2>
            <p>{t.trackingPage[message]}</p>
          </div>
        )}
        {status === 'success' && destination && <TrackingResult destination={destination} />}
      </div>
    </div>
  );
}

export { TrackingResult, formatDate, displayWeight };
export default TrackingPanel;
